package com.fantasytenis.shared.data.survivor.api

import com.fantasytenis.shared.core.network.ApiResponse
import com.fantasytenis.shared.core.network.GenericErrorResponse
import com.fantasytenis.shared.core.network.lenientJson
import com.fantasytenis.shared.data.survivor.dto.*
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*

/**
 * Survivor API client.
 *
 * Handles all survivor contest-related HTTP requests.
 */
class SurvivorApi(
    private val httpClient: HttpClient,
    private val baseUrl: String
) {

    // ===== SURVIVOR CONTESTS =====

    /**
     * Get a survivor contest by ID.
     * GET /v2/survivor-contests/:id
     */
    suspend fun getSurvivorContest(contestId: String): ApiResponse<SurvivorContestDto> {
        return try {
            val response = httpClient.get("${baseUrl}survivor-contests/$contestId")

            if (response.status.isSuccess()) {
                val wrapped = response.body<SurvivorApiResponse<SurvivorContestDto>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Contest not found", response.status.value)
                }
            } else {
                val wrapped = response.body<SurvivorApiResponse<SurvivorContestDto>>()
                ApiResponse.Error(wrapped.error ?: "Failed to get contest", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * List survivor contests.
     * GET /v2/survivor-contests
     */
    suspend fun listSurvivorContests(
        status: Int? = null,
        tournamentId: String? = null,
        featuredOnly: Boolean = false,
        limit: Int = 20,
        offset: Int = 0
    ): ApiResponse<List<SurvivorContestDto>> {
        return try {
            val url = buildString {
                append("${baseUrl}survivor-contests?limit=$limit&offset=$offset")
                status?.let { append("&status=$it") }
                tournamentId?.let { append("&tournamentId=$it") }
                if (featuredOnly) append("&featuredOnly=true")
            }

            val response = httpClient.get(url)

            if (response.status.isSuccess()) {
                val wrapped = response.body<SurvivorListResponse<SurvivorContestDto>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Failed to list contests", response.status.value)
                }
            } else {
                val wrapped = response.body<SurvivorListResponse<SurvivorContestDto>>()
                ApiResponse.Error(wrapped.error ?: "Failed to list contests", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    // ===== SURVIVOR ENTRIES =====

    /**
     * Enter a survivor contest.
     * POST /v2/survivor-contests/:id/enter
     */
    suspend fun createSurvivorEntry(contestId: String): ApiResponse<SurvivorEntryDto> {
        return try {
            val response = httpClient.post("${baseUrl}survivor-contests/$contestId/enter") {
                contentType(ContentType.Application.Json)
                setBody("{}")
            }

            // Backend returns HTTP 200 even for errors, check body status first
            val bodyText = response.bodyAsText()
            val errorCheck = lenientJson.decodeFromString<GenericErrorResponse>(bodyText)

            if (errorCheck.isSuccess) {
                val wrapped = lenientJson.decodeFromString<SurvivorApiResponse<SurvivorEntryDto>>(bodyText)
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Failed to create entry", wrapped.status)
                }
            } else {
                ApiResponse.Error(errorCheck.error ?: "Failed to create entry", errorCheck.status)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Get a survivor entry with picks.
     * GET /v2/survivor-entries/:id
     */
    suspend fun getSurvivorEntry(entryId: String): ApiResponse<SurvivorEntryWithPicksDto> {
        return try {
            val response = httpClient.get("${baseUrl}survivor-entries/$entryId")

            if (response.status.isSuccess()) {
                val wrapped = response.body<SurvivorEntryWithPicksDto>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Entry not found", response.status.value)
                }
            } else {
                val wrapped = response.body<SurvivorEntryWithPicksDto>()
                ApiResponse.Error(wrapped.error ?: "Failed to get entry", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Get user's survivor entries.
     * GET /v2/users/me/survivor-entries
     */
    suspend fun getUserSurvivorEntries(contestId: String? = null): ApiResponse<List<SurvivorEntryDto>> {
        return try {
            val url = buildString {
                append("${baseUrl}users/me/survivor-entries")
                contestId?.let { append("?contestId=$it") }
            }

            val response = httpClient.get(url)

            if (response.status.isSuccess()) {
                val wrapped = response.body<SurvivorListResponse<SurvivorEntryDto>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Failed to get entries", response.status.value)
                }
            } else {
                val wrapped = response.body<SurvivorListResponse<SurvivorEntryDto>>()
                ApiResponse.Error(wrapped.error ?: "Failed to get entries", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Rebuy into a survivor contest.
     * POST /v2/survivor-entries/:id/rebuy
     */
    suspend fun rebuySurvivorEntry(entryId: String): ApiResponse<SurvivorEntryDto> {
        return try {
            val response = httpClient.post("${baseUrl}survivor-entries/$entryId/rebuy") {
                contentType(ContentType.Application.Json)
                setBody("{}")
            }

            // Backend returns HTTP 200 even for errors, check body status first
            val bodyText = response.bodyAsText()
            val errorCheck = lenientJson.decodeFromString<GenericErrorResponse>(bodyText)

            if (errorCheck.isSuccess) {
                val wrapped = lenientJson.decodeFromString<SurvivorApiResponse<SurvivorEntryDto>>(bodyText)
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Failed to rebuy", wrapped.status)
                }
            } else {
                ApiResponse.Error(errorCheck.error ?: "Failed to rebuy", errorCheck.status)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Cancel/withdraw from a survivor contest.
     * DELETE /v2/survivor-entries/:id
     */
    suspend fun cancelSurvivorEntry(entryId: String): ApiResponse<SurvivorEntryDto> {
        return try {
            val response = httpClient.delete("${baseUrl}survivor-entries/$entryId")

            // Backend returns HTTP 200 even for errors, check body status first
            val bodyText = response.bodyAsText()
            val errorCheck = lenientJson.decodeFromString<GenericErrorResponse>(bodyText)

            if (errorCheck.isSuccess) {
                val wrapped = lenientJson.decodeFromString<SurvivorApiResponse<SurvivorEntryDto>>(bodyText)
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Failed to cancel entry", wrapped.status)
                }
            } else {
                ApiResponse.Error(errorCheck.error ?: "Failed to cancel entry", errorCheck.status)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    // ===== PICKS =====

    /**
     * Get available players for picking.
     * GET /v2/survivor-entries/:id/available-players
     */
    suspend fun getAvailablePlayers(entryId: String, round: Int? = null): ApiResponse<List<AvailablePlayerDto>> {
        return try {
            val url = buildString {
                append("${baseUrl}survivor-entries/$entryId/available-players")
                round?.let { append("?round=$it") }
            }

            val response = httpClient.get(url)

            if (response.status.isSuccess()) {
                val wrapped = response.body<AvailablePlayersResponseDto>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Failed to get players", response.status.value)
                }
            } else {
                val wrapped = response.body<AvailablePlayersResponseDto>()
                ApiResponse.Error(wrapped.error ?: "Failed to get players", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Make a survivor pick.
     * POST /v2/survivor-entries/:id/picks
     */
    suspend fun makeSurvivorPick(entryId: String, request: MakeSurvivorPickRequest): ApiResponse<SurvivorPickDto> {
        return try {
            val response = httpClient.post("${baseUrl}survivor-entries/$entryId/picks") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }

            // Backend returns HTTP 200 even for errors, check body status first
            val bodyText = response.bodyAsText()
            val errorCheck = lenientJson.decodeFromString<GenericErrorResponse>(bodyText)

            if (errorCheck.isSuccess) {
                val wrapped = lenientJson.decodeFromString<SurvivorApiResponse<SurvivorPickDto>>(bodyText)
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Failed to make pick", wrapped.status)
                }
            } else {
                ApiResponse.Error(errorCheck.error ?: "Failed to make pick", errorCheck.status)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Update a survivor pick.
     * PUT /v2/survivor-entries/:id/picks/:pickId
     */
    suspend fun updateSurvivorPick(
        entryId: String,
        pickId: String,
        request: UpdateSurvivorPickRequest
    ): ApiResponse<SurvivorPickDto> {
        return try {
            val response = httpClient.put("${baseUrl}survivor-entries/$entryId/picks/$pickId") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }

            // Backend returns HTTP 200 even for errors, check body status first
            val bodyText = response.bodyAsText()
            val errorCheck = lenientJson.decodeFromString<GenericErrorResponse>(bodyText)

            if (errorCheck.isSuccess) {
                val wrapped = lenientJson.decodeFromString<SurvivorApiResponse<SurvivorPickDto>>(bodyText)
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Failed to update pick", wrapped.status)
                }
            } else {
                ApiResponse.Error(errorCheck.error ?: "Failed to update pick", errorCheck.status)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    // ===== LEADERBOARD & ROUND INFO =====

    /**
     * Get survivor contest leaderboard.
     * GET /v2/survivor-contests/:id/leaderboard
     */
    suspend fun getSurvivorLeaderboard(
        contestId: String,
        statusFilter: String? = null,
        limit: Int = 100,
        offset: Int = 0
    ): ApiResponse<SurvivorLeaderboardResponseDto> {
        return try {
            val url = buildString {
                append("${baseUrl}survivor-contests/$contestId/leaderboard?limit=$limit&offset=$offset")
                statusFilter?.let { append("&statusFilter=$it") }
            }

            val response = httpClient.get(url)

            if (response.status.isSuccess()) {
                val wrapped = response.body<SurvivorLeaderboardResponseDto>()
                ApiResponse.Success(wrapped)
            } else {
                val wrapped = response.body<SurvivorLeaderboardResponseDto>()
                ApiResponse.Error(wrapped.error ?: "Failed to get leaderboard", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Get survivor contest round summaries.
     * GET /v2/survivor-contests/:id/rounds
     */
    suspend fun getSurvivorRoundSummary(contestId: String): ApiResponse<List<SurvivorRoundSummaryDto>> {
        return try {
            val response = httpClient.get("${baseUrl}survivor-contests/$contestId/rounds")

            if (response.status.isSuccess()) {
                val wrapped = response.body<SurvivorRoundSummaryResponseDto>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Failed to get round summaries", response.status.value)
                }
            } else {
                val wrapped = response.body<SurvivorRoundSummaryResponseDto>()
                ApiResponse.Error(wrapped.error ?: "Failed to get round summaries", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    // ===== ADMIN: LOCK OVERRIDE =====

    /**
     * Set lock override for a survivor contest (SuperAdmin only).
     * PUT /v2/admin/survivor-contests/:id/lock-override
     */
    suspend fun setLockOverride(
        contestId: String,
        request: SetSurvivorLockOverrideRequest
    ): ApiResponse<SurvivorContestDto> {
        return try {
            val response = httpClient.put("${baseUrl}admin/survivor-contests/$contestId/lock-override") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }

            if (response.status.isSuccess()) {
                val wrapped = response.body<SetSurvivorLockOverrideResponse>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Failed to set lock override", response.status.value)
                }
            } else {
                val wrapped = response.body<SetSurvivorLockOverrideResponse>()
                ApiResponse.Error(wrapped.error ?: "Failed to set lock override", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    // ===== ADMIN: DELETE ENTRY =====

    /**
     * Admin delete a survivor entry (SuperAdmin only).
     * DELETE /v2/admin/survivor-entries/:entryId
     */
    suspend fun adminDeleteSurvivorEntry(
        entryId: String,
        request: AdminDeleteSurvivorEntryRequest
    ): ApiResponse<AdminDeleteSurvivorEntryResponse> {
        return try {
            val response = httpClient.delete("${baseUrl}admin/survivor-entries/$entryId") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }

            if (response.status.isSuccess()) {
                val wrapped = response.body<AdminDeleteSurvivorEntryResponse>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Failed to delete entry", response.status.value)
                }
            } else {
                val wrapped = response.body<AdminDeleteSurvivorEntryResponse>()
                ApiResponse.Error(wrapped.error ?: "Failed to delete entry", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }
}
